// File: /var/www/app1/frontend/src/App.js

import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import Login from "./components/Login";
import AdminDashboard from "./components/AdminDashboard";
import MissionaryForm from "./components/MissionaryForm";
import History from "./components/History";
import UserManagement from "./components/UserManagement";
import SupportRequest from "./components/SupportRequest";
import SupportAdmin from "./components/SupportAdmin";
import axios from "axios";
import "./App.css";

// Configure axios defaults
axios.defaults.baseURL =
  process.env.NODE_ENV === "production"
    ? ""
    : "http://localhost:3001";
axios.defaults.withCredentials = true;

// Singleton lock: when a refresh is in-flight, all callers share the same promise
let refreshPromise = null;

// Timer ID for proactive token refresh
let refreshTimerId = null;

// Guard to prevent duplicate interceptor registration on remount
let interceptorSetUp = false;

/**
 * Parse JWT expiry time without verifying the signature.
 * Returns the expiry timestamp in milliseconds, or null if unparseable.
 */
function parseJwtExpiry(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    const decoded = JSON.parse(atob(payload));
    if (typeof decoded.exp === 'number') {
      return decoded.exp * 1000;
    }
    return null;
  } catch {
    return null;
  }
}

/**
 * Parse JWT payload without verifying
 */
function parseJwtPayload(token) {
  try {
    const payload = token.split('.')[1];
    if (!payload) return null;
    return JSON.parse(atob(payload));
  } catch {
    return null;
  }
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(false);

  const scheduleTokenRefresh = (token) => {
    if (refreshTimerId) {
      clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
    const expiryMs = parseJwtExpiry(token);
    if (!expiryMs) return;
    const now = Date.now();
    const lifetime = expiryMs - now;
    if (lifetime <= 0) return;
    const refreshIn = Math.floor(lifetime * 0.75);
    refreshTimerId = setTimeout(() => {
      refreshToken().catch(() => {});
    }, refreshIn);
  };

  useEffect(() => {
    checkAuth();
    if (!interceptorSetUp) {
      setupAxiosInterceptors();
      interceptorSetUp = true;
    }
    return () => {
      if (refreshTimerId) {
        clearTimeout(refreshTimerId);
        refreshTimerId = null;
      }
    };
  }, []);

  const refreshToken = () => {
    // Don't refresh during impersonation (short-lived token, no refresh)
    if (sessionStorage.getItem('adminToken')) {
      return Promise.reject(new Error('Impersonation session - no refresh'));
    }

    if (refreshPromise) {
      return refreshPromise;
    }

    refreshPromise = (async () => {
      try {
        const refreshTokenValue = localStorage.getItem('refreshToken');
        if (!refreshTokenValue) {
          throw new Error('No refresh token available');
        }

        const response = await axios.post("/api/auth/refresh", {
          refreshToken: refreshTokenValue
        });

        if (response.data.success) {
          localStorage.setItem('accessToken', response.data.accessToken);
          localStorage.setItem('refreshToken', response.data.refreshToken);
          axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.accessToken}`;
          scheduleTokenRefresh(response.data.accessToken);
          return response.data.accessToken;
        }

        throw new Error('Token refresh failed');
      } catch (error) {
        console.error("Token refresh failed:", error);
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        throw error;
      } finally {
        refreshPromise = null;
      }
    })();

    return refreshPromise;
  };

  const setupAxiosInterceptors = () => {
    axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        // Don't intercept 401s from the refresh endpoint itself
        if (originalRequest?.url?.includes('/api/auth/refresh')) {
          return Promise.reject(error);
        }

        // During impersonation, 401 means the short-lived token expired — exit impersonation
        if (error.response?.status === 401 && sessionStorage.getItem('adminToken')) {
          handleExitImpersonation();
          return Promise.reject(error);
        }

        if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
          originalRequest._retry = true;

          try {
            const newToken = await refreshToken();
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          } catch (refreshError) {
            console.error("Token refresh failed in interceptor:", refreshError);
            return Promise.reject(error);
          }
        }

        return Promise.reject(error);
      }
    );
  };

  const checkAuth = async () => {
    try {
      // Check if we're in an impersonation session
      const adminToken = sessionStorage.getItem('adminToken');
      if (adminToken) {
        setImpersonating(true);
      }

      const token = localStorage.getItem('accessToken');
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        const response = await axios.get("/api/auth/me");
        if (response.data.success) {
          setUser(response.data.user);
          if (!adminToken) {
            scheduleTokenRefresh(token);
          }
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);

      // If impersonation token expired, exit impersonation
      if (error.response?.status === 401 && sessionStorage.getItem('adminToken')) {
        handleExitImpersonation();
        return;
      }

      // If token expired, try to refresh
      if (error.response?.status === 401) {
        try {
          await refreshToken();
          // Retry the auth check with new token
          const response = await axios.get("/api/auth/me");
          if (response.data.success) {
            setUser(response.data.user);
            return;
          }
        } catch (refreshError) {
          console.error("Token refresh failed during auth check:", refreshError);
        }
      }

      // Clear tokens if refresh failed or other error
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData, tokens) => {
    setUser(userData);
    if (tokens) {
      localStorage.setItem('accessToken', tokens.accessToken);
      localStorage.setItem('refreshToken', tokens.refreshToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${tokens.accessToken}`;
      scheduleTokenRefresh(tokens.accessToken);
    }
  };

  const handleLogout = async () => {
    if (refreshTimerId) {
      clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }
    // If impersonating, just exit impersonation instead
    if (impersonating) {
      handleExitImpersonation();
      return;
    }
    try {
      await axios.post("/api/auth/logout");
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    } catch (error) {
      console.error("Logout failed:", error);
      // Clear local storage even if logout request fails
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      delete axios.defaults.headers.common['Authorization'];
      setUser(null);
    }
  };

  const handleImpersonate = (impersonationToken, targetUser) => {
    // Save admin's current tokens
    const currentAccessToken = localStorage.getItem('accessToken');
    const currentRefreshToken = localStorage.getItem('refreshToken');
    sessionStorage.setItem('adminToken', currentAccessToken);
    sessionStorage.setItem('adminRefreshToken', currentRefreshToken);

    // Switch to impersonation token
    localStorage.setItem('accessToken', impersonationToken);
    axios.defaults.headers.common['Authorization'] = `Bearer ${impersonationToken}`;

    // Cancel scheduled refresh (impersonation tokens are short-lived, no refresh)
    if (refreshTimerId) {
      clearTimeout(refreshTimerId);
      refreshTimerId = null;
    }

    // Update user state
    const payload = parseJwtPayload(impersonationToken);
    setUser({
      id: targetUser.id,
      username: targetUser.username,
      role: targetUser.role,
      missionary_id: payload?.missionary_id || null,
      session_id: payload?.session_id || '',
    });
    setImpersonating(true);
  };

  const handleExitImpersonation = async () => {
    try {
      // Try to end the impersonation session on the server
      await axios.post("/api/auth/stop-impersonate").catch(() => {});
    } catch {}

    // Restore admin tokens
    const adminToken = sessionStorage.getItem('adminToken');
    const adminRefreshToken = sessionStorage.getItem('adminRefreshToken');

    sessionStorage.removeItem('adminToken');
    sessionStorage.removeItem('adminRefreshToken');

    if (adminToken) {
      localStorage.setItem('accessToken', adminToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${adminToken}`;
      if (adminRefreshToken) {
        localStorage.setItem('refreshToken', adminRefreshToken);
      }
      scheduleTokenRefresh(adminToken);

      // Re-fetch admin user info
      try {
        const response = await axios.get("/api/auth/me");
        if (response.data.success) {
          setUser(response.data.user);
        }
      } catch {
        // If admin token is also expired, redirect to login
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
      }
    } else {
      setUser(null);
    }

    setImpersonating(false);
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  const isAdmin = user && (user.role === 'admin' || user.role === 'superadmin');

  return (
    <Router>
      <div className="App">
        {/* Impersonation Banner */}
        {impersonating && user && (
          <div style={{
            backgroundColor: "#d69e2e",
            color: "white",
            padding: "8px 20px",
            textAlign: "center",
            fontWeight: "bold",
            fontSize: "14px",
            position: "sticky",
            top: 0,
            zIndex: 1000,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "15px",
          }}>
            <span>Viewing as {user.username}</span>
            <button
              onClick={handleExitImpersonation}
              style={{
                backgroundColor: "white",
                color: "#d69e2e",
                border: "none",
                borderRadius: "4px",
                padding: "4px 12px",
                cursor: "pointer",
                fontWeight: "bold",
                fontSize: "13px",
              }}
            >
              Exit Impersonation
            </button>
          </div>
        )}

        {user && (
          <nav className="navbar">
            <div className="nav-container">
              <h1>Monthly Report System</h1>
              <div className="nav-items">
                <span>Welcome, {user.username}</span>
                <a
                  href="/"
                  style={{
                    color: "#667eea",
                    marginRight: "15px",
                    textDecoration: "none",
                    fontWeight: "500",
                  }}
                >
                  Dashboard
                </a>
                {isAdmin && (
                  <a
                    href="/history"
                    style={{
                      color: "#667eea",
                      marginRight: "15px",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    History
                  </a>
                )}
                {isAdmin && !impersonating && (
                  <a
                    href="/users"
                    style={{
                      color: "#667eea",
                      marginRight: "15px",
                      textDecoration: "none",
                      fontWeight: "500",
                    }}
                  >
                    Users
                  </a>
                )}
                {!impersonating && isAdmin && (
                  <>
                    {user.role === "superadmin" ? (
                      <a href="/support-admin" className="nav-link">
                        Support Admin
                      </a>
                    ) : (
                      <a href="/support" className="nav-link">
                        Support
                      </a>
                    )}
                  </>
                )}

                <button onClick={handleLogout} className="logout-btn">
                  {impersonating ? "Exit" : "Logout"}
                </button>
              </div>
            </div>
          </nav>
        )}

        <Routes>
          <Route
            path="/login"
            element={
              user ? <Navigate to="/" /> : <Login onLogin={handleLogin} />
            }
          />

          <Route
            path="/"
            element={!user ? <Navigate to="/login" /> : <AdminDashboard user={user} />}
          />

          <Route
            path="/history"
            element={!user ? <Navigate to="/login" /> : (
              isAdmin ? <History user={user} /> : <Navigate to="/" />
            )}
          />

          <Route
            path="/users"
            element={!user ? <Navigate to="/login" /> : (
              isAdmin && !impersonating
                ? <UserManagement user={user} onImpersonate={handleImpersonate} />
                : <Navigate to="/" />
            )}
          />

          <Route
            path="/support"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : isAdmin ? (
                <SupportRequest user={user} />
              ) : (
                <Navigate to="/" />
              )
            }
          />

          <Route
            path="/support-admin"
            element={
              !user ? (
                <Navigate to="/login" />
              ) : user.role === "superadmin" ? (
                <SupportAdmin user={user} />
              ) : user.role === "admin" ? (
                <Navigate to="/support" />
              ) : (
                <Navigate to="/" />
              )
            }
          />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
